import { createReducer } from 'typesafe-actions';

import { ECompanyType } from 'shared/repositories/coworking/entities/api/PublicationRequestSchema';

import { EMAIL_VALIDATION_REG_EX, PHONE_VALIDATION_REG_EX } from '../../constants/form/validation';

import {
  setCompanyNameFieldValueAction,
  setCompanyTypeFieldValueAction,
  setEmailFieldValueAction,
  setPhoneFieldValueAction,
  validateAllAction,
  validateByFieldAction,
} from './actions';
import { EPlacementApplicationFormFields } from './constants';
import {
  TPlacementApplicationFormActionsType,
  TPlacementApplicationFormReducer,
  TPlacementApplicationFormState,
  TValidationRules,
} from './types';

export const getPlacementApplicationFormInitialState = (
  fields?: Partial<TPlacementApplicationFormState>,
): TPlacementApplicationFormState => ({
  [EPlacementApplicationFormFields.CompanyName]: { errorMessage: null, value: '' },
  [EPlacementApplicationFormFields.CompanyType]: { errorMessage: null, value: ECompanyType.Operator },
  [EPlacementApplicationFormFields.Email]: { errorMessage: null, value: '' },
  [EPlacementApplicationFormFields.Phone]: { errorMessage: null, value: '' },
  ...fields,
});

export const validationRules: TValidationRules = {
  [EPlacementApplicationFormFields.Email]: (value: string) => {
    if (value.length === 0) {
      return 'Введите e-mail';
    }

    if (value.length > 254) {
      return 'Максимальное количество символов – 254';
    }

    if (EMAIL_VALIDATION_REG_EX.test(value)) {
      return null;
    }

    return 'Введите корректный e-mail';
  },
  [EPlacementApplicationFormFields.CompanyType]: (value: ECompanyType) => {
    if (value.length === 0) {
      return 'Выберите вид деятельности';
    }

    return null;
  },
  [EPlacementApplicationFormFields.Phone]: (value: string) => {
    if (value.length === 0) {
      return 'Введите номер телефона';
    }

    if (PHONE_VALIDATION_REG_EX.test(value)) {
      return null;
    }

    return 'Введите корректный номер телефона';
  },
  [EPlacementApplicationFormFields.CompanyName]: (value: string) => {
    if (value.length === 0) {
      return 'Введите название компании';
    }

    if (value.length > 200) {
      return 'Максимальное количество символов – 200';
    }

    return null;
  },
};

const placementApplicationFormBaseReducer = createReducer<
  TPlacementApplicationFormState,
  TPlacementApplicationFormActionsType
>(getPlacementApplicationFormInitialState())
  .handleAction(
    [setEmailFieldValueAction, setPhoneFieldValueAction, setCompanyNameFieldValueAction],
    (state, action) => {
      state[action.type].value = action.payload;
      state[action.type].errorMessage = null;

      return { ...state };
    },
  )
  .handleAction(setCompanyTypeFieldValueAction, (state, action) => {
    state[action.type].value = action.payload;
    state[action.type].errorMessage = null;

    return { ...state };
  })
  .handleAction(validateByFieldAction, (state, action) => {
    state[action.payload].errorMessage = validationRules[action.payload](state[action.payload].value, state);

    return { ...state };
  })
  .handleAction(validateAllAction, state => {
    const validationRulesKeys = Object.keys(validationRules) as Array<EPlacementApplicationFormFields>;

    validationRulesKeys.forEach(key => {
      state[key].errorMessage = validationRules[key](state[key].value, state);
    });

    return { ...state };
  });

export const placementApplicationFormReducer: TPlacementApplicationFormReducer = (state, action) =>
  placementApplicationFormBaseReducer.handlers[action.type](state, action);
